import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, CircularProgress, Typography } from '@mui/material';

import { MinimalAuth0Provider } from '../auth0-provider';
import APIService from '../services/APIService';

/**
 * Inner component that handles the Google OAuth callback
 * This runs inside the Auth0Provider context
 */
const GoogleAuthCallbackHandler: React.FC = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently, isAuthenticated, isLoading, user } = useAuth0();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [processed, setProcessed] = useState(false);

  useEffect(() => {
    if (processed) return;

    const handleCallback = async () => {
      try {
        // Wait for Auth0 to be ready
        if (isLoading) return;

        // If not authenticated, try to get the token silently which will use existing session
        if (!isAuthenticated) {
          console.warn('Not authenticated, attempting silent authentication...');
          try {
            await getAccessTokenSilently();
          } catch (error) {
            console.error('Silent authentication failed:', error);
            // Store the Google OAuth params and redirect to login
            const currentParams = new URLSearchParams(searchParams);
            sessionStorage.setItem('google_oauth_params', currentParams.toString());
            navigate('/');
            return;
          }
        }

        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');

        if (error) {
          console.error('Google authorization error:', error);
          setStatus('error');
          setTimeout(() => {
            navigate('/user');
          }, 2000);
          return;
        }

        if (!code || !state) {
          setStatus('error');
          setTimeout(() => {
            navigate('/user');
          }, 2000);
          return;
        }

        const storedState = localStorage.getItem('google_oauth_state');
        if (state !== storedState) {
          console.error("State parameter doesn't match stored state");
          setStatus('error');
          setTimeout(() => {
            navigate('/user');
          }, 2000);
          return;
        }

        localStorage.removeItem('google_oauth_state');
        setProcessed(true);

        // Get the access token
        const accessToken = await getAccessTokenSilently();
        const userId = user?.sub?.replace('auth0|', '');

        // Exchange the authorization code with our backend
        const redirectUri = `${window.location.origin}/google-auth-callback`;
        const response = await APIService.makeAPIPostRequest({
          requestString: `/v2/provider/integrations/google`,
          accessToken: accessToken,
          body: {
            code: code,
            provider_id: userId,
            redirect_uri: redirectUri,
          },
        });

        if (!response.ok) {
          throw new Error(response.error?.message || 'Failed to exchange authorization code');
        }

        setStatus('success');
        setTimeout(() => {
          navigate('/user');
        }, 2000);
      } catch (error) {
        console.error('Error handling Google OAuth callback:', error);
        setStatus('error');
        setTimeout(() => {
          navigate('/user');
        }, 2000);
      }
    };

    handleCallback();
  }, [searchParams, navigate, getAccessTokenSilently, isAuthenticated, isLoading, user, processed]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: 2,
      }}
    >
      {status === 'loading' && (
        <>
          <CircularProgress size={60} />
          <Typography variant="h5">Connecting Google Calendar...</Typography>
        </>
      )}
      {status === 'success' && (
        <>
          <Typography variant="h5" color="success.main">
            Google Calendar successfully connected!
          </Typography>
          <Typography>Redirecting to your profile...</Typography>
        </>
      )}
      {status === 'error' && (
        <>
          <Typography variant="h5" color="error.main">
            Failed to connect Google Calendar
          </Typography>
          <Typography>Redirecting to your profile...</Typography>
        </>
      )}
    </Box>
  );
};

/**
 * Wrapper component for GoogleAuthCallback that provides Auth0 context
 * without going through the full app initialization flow.
 * Uses MinimalAuth0Provider which has Auth0 context without heavy app providers.
 */
export const GoogleAuthCallbackWrapper: React.FC = () => {
  return (
    <MinimalAuth0Provider>
      <GoogleAuthCallbackHandler />
    </MinimalAuth0Provider>
  );
};
